import { ref, reactive, computed, watch, getCurrentInstance } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import dayjs from 'dayjs';
import { twoBtnDialog } from "@/utils/utils";

export default function useEventForm(api) {
  const router = useRouter();
  const route = useRoute();
  const formRef = ref(null);
  const submitting = ref(false);
  const activeTab = ref('share');
  const showSearchModal = ref(false); // 거래처 검색 모달 표시 상태
  const isDirty = ref(false); // 폼 수정 여부 추적
  const { proxy: app } = getCurrentInstance();
  
  // 수정 모드인지 확인
  const isEdit = computed(() => route.params.id !== undefined);
  
  // 유효한 이미지 확장자 목록
  const validImageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  
  // 폼 데이터 모델 (기본 정보만 유지)
  const form = reactive({
    // 이벤트 기본 정보
    id: isEdit.value ? route.params.id : null,
    organizers: [], // 복수의 이벤트 진행주체 배열
    directInput: false, // 직접 입력 모드
    eventTitle: '',
    scenarioType: 'review', // 기본값: review | share | custom
    customScenarioType: '', // scenarioType이 custom일 때 사용
    startDate: null,
    endDate: null,
    eventStatus: 'preparing',
    description: '',
    envConfigKey: '', // 환경변수 설정 키 (예: ZEISS_EVENT_2025_FALL)
    alimtalkTemplateCode: '', // 알림톡 템플릿 코드 (예: DEV_ZEISS_EVENT_202509_V3)
    targetScope: 'ALL', // 이벤트 적용 범위 (ALL: 모든 병원, SPECIFIC: 특정 병원만)
    targetHospitals: [], // 적용 대상 병원 목록 [{ hospitalId, hospitalName }] (targetScope가 SPECIFIC일 때 사용)
    // 과거 이벤트 참여자 차단 설정 (유연한 범위 설정)
    blockPreviousParticipants: {
      enabled: false,              // 기능 활성화 여부
      checkAll: false,             // 모든 이벤트에서 체크 (전역)
      checkSameOrganizer: false,   // 같은 주최자의 이벤트에서 체크 (organizer 하나라도 일치 시)
      checkSpecificOrganizers: false, // 특정 주최자들의 이벤트에서 체크
      checkSpecificEvents: false,  // 특정 이벤트들에서 체크
      targetOrganizerIds: [],      // 체크 대상 주최자 ID 목록 (checkSpecificOrganizers용)
      targetEventIds: [],          // 체크 대상 이벤트 ID 목록 (checkSpecificEvents용)
      // 각 스코프별 사용자 친화 메시지 (차단 시 사용자에게 표시)
      checkAllMessage: '감사합니다! 이미 저희 이벤트에 참여해 주셨네요. 한 분당 한 번만 참여 가능한 이벤트입니다. 다음 이벤트에서 또 만나요! 🎉',
      checkSameOrganizerMessage: '감사합니다! 이미 저희 이벤트에 참여해 주셨네요. 동일 주최자 이벤트는 한 번만 참여 가능합니다. 다음 기회에 만나요! 🎉',
      checkSpecificOrganizersMessage: '감사합니다! 이미 관련 이벤트에 참여해 주셨네요. 연계 이벤트는 한 번만 참여 가능합니다. 다음에 또 만나요! 🎉',
      checkSpecificEventsMessage: '감사합니다! 이미 관련 이벤트에 참여해 주셨네요. 연계 이벤트는 한 번만 참여 가능합니다. 다음에 또 만나요! 🎉',
      segmentFilter: 'ALL',        // 세그먼트 필터 (ALL: 전체, CERTIFICATE_HOLDER: 인증서 보유자만, NON_CERTIFICATE: 미보유자만)
    },
    isCouponDepleted: false, // 쿠폰 재고 소진 여부 (true: 쿠폰 발급 차단, false: 발급 가능)
    couponDepletedMessage: '쿠폰이 모두 소진되었습니다. 조금만 기다려 주세요.', // 쿠폰 소진 시 사용자에게 표시할 메시지
    // 환자/친구별 최대 쿠폰 수량 설정
    couponLimits: {
      patient: { maxCoupons: 2, description: '' },  // 환자 최대 쿠폰 수
      friend: { maxCoupons: 2, description: '' }    // 친구 최대 쿠폰 수
    },
    isReportVisible: false, // 고객사 레포트 노출 여부 (true: 레포트 페이지 이벤트 필터에 노출)
    // 대량 참여자 생성 설정
    bulkAttendeeCreation: {
      enabled: false,
      targetType: 'all', // all | nft_holders | specific
      filters: {
        companyIds: [],
        excludeCustomerIds: [],
        createdAfter: null,
        createdBefore: null
      },
      options: {
        batchSize: 3000,
        concurrency: 5
      }
    },
    // 페이지 리소스 관리
    pageResources: [],
    // 카카오 공유하기 버튼 설정
    kakaoShareBtn: {
      title: '',
      description: '',
      imageUrl: '',
      buttonText: '',
      btnLink: ''
    },
    // 쿠폰함 리소스 설정 (배열 형식)
    couponBoxResources: [
      {
        resourceId: 'patient-banner',
        resourceType: 'PATIENT_BANNER',
        name: '환자용 배너',
        url: '',
        isActive: true
      },
      {
        resourceId: 'patient-popup',
        resourceType: 'PATIENT_POPUP',
        name: '환자용 팝업',
        url: '',
        isActive: true
      },
      {
        resourceId: 'friend-banner',
        resourceType: 'FRIEND_BANNER',
        name: '친구용 배너',
        url: '',
        isActive: true
      },
      {
        resourceId: 'friend-popup',
        resourceType: 'FRIEND_POPUP',
        name: '친구용 팝업',
        url: '',
        isActive: true
      }
    ],
    // 이탈방지 팝업 설정
    exitPreventionPopup: {
      enabled: false,
      imageUrl: '',
      clickAction: {
        actionType: 'none',
        url: '',
        target: '_blank'
      }
    },
    // 계층 구조 페이지 설정 (Page > Step > Section > Component)
    eventPages: [],
    // 보상 임계치 설정 (배열)
    rewardThresholds: [],
    // AARRR 메트릭 설정
    aarrrMetrics: {
      acquisition: [],
      activation: [],
      retention: [],
      revenue: [],
      referral: []
    },
    // 예산 관리
    budgetRounds: [], // 예산 회차 목록
    totalBudget: 0, // 총 예산 (자동 계산)
    budgetNote: '', // 예산 관련 메모
    // 지출 관리
    expenses: [], // 지출 내역 목록
    // 인증서 수신 페이지 모드 (REQ-R3-C2: 신규 생성 기본값 'event')
    certificateReceivePageType: 'event',
    // 쿠폰함 페이지 이벤트 유도 팝업 표시 여부 (REQ-R2-C4: 신규 생성 기본값 true)
    couponPageEventModalEnabled: true,
    // 후기 화면 (default: 기본형 / zeiss-v1: 자이스 후기 v1). 신규 생성 기본값 'default'
    reviewPageType: 'default'
  });

  // eventPages 초기 구조 설정
  const initializeEventPages = () => {
    return [
      {
        pageId: 'certificateCustomerPage',
        pageType: 'certificateCustomer',
        pageName: '인증서 고객 페이지',
        order: 1,  // order 필드 추가 (API에 추가됨)
        isActive: true,
        steps: [
          {
            stepId: 'mainStep',
            stepType: 'main',
            stepName: '메인',
            order: 1,
            isActive: true,
            sections: [
              {
                sectionId: 'mainSection',
                sectionName: '메인 섹션',  // sectionName 필드 추가
                sectionType: 'content',
                order: 1,
                components: [],
                metadata: {}
              }
            ]
          }
        ]
      },
      {
        pageId: 'friendPage',
        pageType: 'friend',
        pageName: '친구 페이지',
        order: 2,  // order 필드 추가 (API에 추가됨)
        isActive: true,
        steps: [
          {
            stepId: 'mainStep',
            stepType: 'main',
            stepName: '메인',
            order: 1,
            isActive: true,
            sections: [
              {
                sectionId: 'quizSection',
                sectionName: '퀴즈 섹션',  // sectionName 필드 추가
                sectionType: 'quiz',
                order: 1,
                metadata: {
                  quizCompleteCardImg: ''
                },
                components: []
              },
              {
                sectionId: 'contentSection',
                sectionName: '콘텐츠 섹션',  // sectionName 필드 추가
                sectionType: 'content',
                order: 2,
                components: [],
                metadata: {}
              }
            ]
          }
        ]
      },
      {
        pageId: 'hospitalDetailPage',
        pageType: 'hospitalDetail',
        pageName: '병원 상세 페이지',
        order: 3,
        isActive: true,
        steps: [
          {
            stepId: 'mainStep',
            stepType: 'main',
            stepName: '메인',
            order: 1,
            isActive: true,
            sections: [
              {
                sectionId: 'mainSection',
                sectionName: '메인 섹션',
                sectionType: 'content',
                order: 1,
                components: [],
                metadata: {}
              }
            ]
          }
        ]
      },
      {
        pageId: 'reviewModal',
        pageType: 'hospitalReviewModal',
        pageName: '후기 모달',
        order: 4,
        isActive: true,
        steps: [
          {
            stepId: 'mainStep',
            stepType: 'main',
            stepName: '메인',
            order: 1,
            isActive: true,
            sections: [
              {
                sectionId: 'mainSection',
                sectionName: '메인 섹션',
                sectionType: 'content',
                order: 1,
                components: [],
                metadata: {}
              }
            ]
          }
        ]
      }
    ];
  };

  // eventPages 초기화
  if (!form.eventPages || form.eventPages.length === 0) {
    form.eventPages = initializeEventPages();
  }
  
  // 유효성 검사 규칙
  const rules = {
    organizers: [{ 
      required: true, 
      message: '최소 하나 이상의 이벤트 진행주체를 선택해주세요', 
      validator: (rule, value) => {
        if (!value || value.length === 0) {
          return Promise.reject('최소 하나 이상의 이벤트 진행주체를 선택해주세요');
        }
        return Promise.resolve();
      },
      trigger: 'change' 
    }],
    eventTitle: [
      { required: true, message: '이벤트 제목을 입력해주세요', trigger: 'blur' },
      { min: 2, max: 100, message: '이벤트 제목은 2-100자 이내로 입력해주세요', trigger: 'blur' }
    ],
    customScenarioType: [{ 
      required: true, 
      message: '시나리오 유형을 입력해주세요', 
      trigger: 'blur',
      validator: (rule, value) => {
        if (form.scenarioType === 'custom' && !value) {
          return Promise.reject('시나리오 유형을 입력해주세요');
        }
        return Promise.resolve();
      } 
    }],
    startDate: [
      { required: true, message: '시작일을 선택해주세요', trigger: 'change' },
      { 
        validator: (rule, value) => {
          if (value && form.endDate && dayjs(value).isAfter(form.endDate)) {
            return Promise.reject('시작일은, 종료일보다 이전이어야 합니다');
          }
          return Promise.resolve();
        }, 
        trigger: 'change' 
      }
    ],
    endDate: [
      { required: true, message: '종료일을 선택해주세요', trigger: 'change' },
      { 
        validator: (rule, value) => {
          if (value && form.startDate && dayjs(value).isBefore(form.startDate)) {
            return Promise.reject('종료일은, 시작일보다 이후여야 합니다');
          }
          return Promise.resolve();
        }, 
        trigger: 'change' 
      }
    ],
    // targetCustomers 필드 제거 - bulkAttendeeCreation.targetType으로 대체
  };
  
  // 날짜 관계 검증을 위한 watch 설정
  watch(() => form.startDate, (newVal) => {
    if (formRef.value && newVal) {
      formRef.value.validateFields(['endDate', 'announcementDate']);
    }
  });

  watch(() => form.endDate, (newVal) => {
    if (formRef.value && newVal) {
      formRef.value.validateFields(['startDate', 'announcementDate']);
    }
  });

  // 폼 변경 감지 - 주요 필드들만 감시하여 isDirty 설정
  watch(
    () => [
      form.eventTitle,
      form.scenarioType,
      form.customScenarioType,
      form.startDate,
      form.endDate,
      form.eventStatus,
      form.description,
      form.certificateReceivePageType, // REQ-R3-C4: 라디오 변경 시 dirty 전환
      form.couponPageEventModalEnabled, // REQ-R2-C6: 체크박스 변경 시 dirty 전환
      form.reviewPageType, // 후기 화면 라디오 변경 시 dirty 전환
      JSON.stringify(form.organizers),
      JSON.stringify(form.kakaoShareBtn),
      JSON.stringify(form.couponBoxResources),
      JSON.stringify(form.exitPreventionPopup),
      JSON.stringify(form.eventPages),
      JSON.stringify(form.rewardThresholds),
      JSON.stringify(form.aarrrMetrics)
    ],
    () => {
      isDirty.value = true;
    },
    { deep: false }
  );

  // 유효성 검사 오류 처리 함수
  const handleValidationError = (error) => {
    const errorFields = error?.errorFields || [];
    if (errorFields.length > 0) {
      // 첫 번째 오류 필드의 이름과 메시지 가져오기
      const firstErrorField = errorFields[0];
      const fieldName = firstErrorField.name[0] || '알 수 없는 필드';
      const errorMessage = firstErrorField.errors[0] || '입력값이 유효하지 않습니다';
      
      // 특정 필드에 대한 더 읽기 쉬운 이름 매핑
      const fieldNameMap = {
        organizers: '이벤트 진행주체',
        eventTitle: '이벤트 제목',
        scenarioType: '시나리오 유형',
        customScenarioType: '시나리오 유형',
        startDate: '시작일',
        endDate: '종료일',
        // targetCustomers 필드 제거
        shareMessage: '공유 메시지',
        androidUrl: 'Android 앱 URL',
        iosUrl: 'iOS 앱 URL',
        surveyTitle: '설문 제목',
        reservationTitle: '예약 안내 제목',
        announcementDate: '당첨자 발표일',
        drawType: '당첨자 선정 방식'
      };
      
      // 토스트 메시지 표시
      app.$message.error(`입력값 오류: ${fieldNameMap[fieldName] || fieldName} - ${errorMessage}`);
      
      // 해당 필드로 스크롤
      setTimeout(() => {
        const errorElement = document.querySelector(`.ant-form-item-has-error`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      app.$message.error('입력 내용을 확인해주세요.');
    }
    console.error('폼 유효성 검사 실패:', error);
  };
  
  // 폼 제출 핸들러
  const handleSubmit = async (values) => {
    try {
      // 폼 유효성 검사는 a-form 컴포넌트의 @finish 이벤트에서 이미 수행되었으므로 여기서는 생략
      // 대신 추가 검증 로직만 수행
      
      submitting.value = true;
      
      
      // organizers 데이터 형식 확인 및 수정
      let validOrganizers = form.organizers.filter(org => org && (org.organizerName || org.name));
      if (validOrganizers.length === 0) {
        app.$message.error('최소 하나 이상의 이벤트 진행주체를 설정해야 합니다.');
        submitting.value = false;
        return;
      }
      
      // organizers 데이터 형식 맞추기 (백엔드 스키마에 맞게)
      validOrganizers = validOrganizers.map(org => {
        // 이미 백엔드 스키마 형식인지 확인
        if (org.organizerName) {
          return {
            organizerId: org.organizerId || null,
            organizerName: org.organizerName,
            type: org.type || 'HOSPITAL',
            representative: org.representative || '',
            organizerCno: org.organizerCno || '',
            directInput: org.directInput === true
          };
        } else {
          // 이전 형식이면 변환
          return {
            organizerId: org.id ? String(org.id) : null,
            organizerName: org.name,
            type: org.type || 'HOSPITAL',
            representative: org.representative || '',
            organizerCno: org.businessNumber || '',
            directInput: org.isCustom === true
          };
        }
      });
      
      // targetCustomers를 bulkAttendeeCreation.targetType에 따라 자동 설정
      let targetCustomersValue = 'all'; // 기본값
      
      if (form.bulkAttendeeCreation?.enabled && form.bulkAttendeeCreation?.targetType) {
        // 참여자 대상 유형에 따라 targetCustomers 매핑
        switch (form.bulkAttendeeCreation.targetType) {
          case 'all':
            targetCustomersValue = 'all';
            break;
          case 'nft_holders':
            targetCustomersValue = 'existing'; // NFT 보유자는 기존 고객으로 매핑
            break;
          case 'specific':
            targetCustomersValue = 'specific';
            break;
          default:
            targetCustomersValue = 'all';
        }
      }
      
      // 서버에서 처리 가능한 속성만 포함하도록 데이터 정리 (기본 정보만)
      const apiData = {
        // 기본 정보만 포함
        organizers: validOrganizers,
        directInput: form.directInput,
        eventTitle: form.eventTitle,
        scenarioType: form.scenarioType === 'custom' ? form.customScenarioType : form.scenarioType,
        // scenarioType이 custom이면 customScenarioType 값을 scenarioType으로 전송
        startDate: form.startDate,
        endDate: form.endDate,
        eventStatus: form.eventStatus,
        targetCustomers: targetCustomersValue, // 자동 설정된 값 사용
        description: form.description,
        envConfigKey: form.envConfigKey || undefined, // 환경변수 설정 키
        alimtalkTemplateCode: form.alimtalkTemplateCode || undefined, // 알림톡 템플릿 코드
        targetScope: form.targetScope || 'ALL', // 이벤트 적용 범위
        targetHospitals: (form.targetScope === 'SPECIFIC' || form.targetScope === 'EXCLUDE') ? form.targetHospitals : [], // 적용 대상/제외 병원 목록
        // 과거 이벤트 참여자 차단 설정 (객체 구조)
        blockPreviousParticipants: form.blockPreviousParticipants?.enabled ? {
          enabled: true,
          checkAll: form.blockPreviousParticipants.checkAll || false,
          checkSameOrganizer: form.blockPreviousParticipants.checkSameOrganizer || false,
          checkSpecificOrganizers: form.blockPreviousParticipants.checkSpecificOrganizers || false,
          checkSpecificEvents: form.blockPreviousParticipants.checkSpecificEvents || false,
          targetOrganizerIds: form.blockPreviousParticipants.targetOrganizerIds || [],
          targetEventIds: form.blockPreviousParticipants.targetEventIds || [],
          // 각 스코프별 사용자 친화 메시지
          checkAllMessage: form.blockPreviousParticipants.checkAllMessage || '',
          checkSameOrganizerMessage: form.blockPreviousParticipants.checkSameOrganizerMessage || '',
          checkSpecificOrganizersMessage: form.blockPreviousParticipants.checkSpecificOrganizersMessage || '',
          checkSpecificEventsMessage: form.blockPreviousParticipants.checkSpecificEventsMessage || '',
          segmentFilter: form.blockPreviousParticipants.segmentFilter || 'ALL',
        } : { enabled: false },
        isCouponDepleted: form.isCouponDepleted || false, // 쿠폰 재고 소진 여부
        couponDepletedMessage: form.couponDepletedMessage || '', // 쿠폰 소진 시 표시할 메시지
        // 환자/친구별 최대 쿠폰 수량 설정
        couponLimits: {
          patient: {
            maxCoupons: form.couponLimits?.patient?.maxCoupons ?? 2,
            description: form.couponLimits?.patient?.description || ''
          },
          friend: {
            maxCoupons: form.couponLimits?.friend?.maxCoupons ?? 2,
            description: form.couponLimits?.friend?.description || ''
          }
        },
        isReportVisible: form.isReportVisible || false, // 고객사 레포트 노출 여부
        // 인증서 수신 페이지 모드 (REQ-R3-C5: PUT payload에 포함)
        certificateReceivePageType: form.certificateReceivePageType || 'event',
        // 쿠폰함 팝업 표시 여부 (REQ-R2-C7: PUT payload에 포함, boolean 타입 유지)
        couponPageEventModalEnabled: form.couponPageEventModalEnabled !== false,
        // 후기 화면 (항상 보낸다. 값이 없으면 기본형)
        reviewPageType: form.reviewPageType || 'default',
      };

      // 카카오 공유하기 버튼 설정 추가 (값이 있는 경우에만)
      if (form.kakaoShareBtn && (
        form.kakaoShareBtn.title ||
        form.kakaoShareBtn.description ||
        form.kakaoShareBtn.imageUrl ||
        form.kakaoShareBtn.buttonText ||
        form.kakaoShareBtn.btnLink
      )) {
        apiData.kakaoShareBtn = {
          title: form.kakaoShareBtn.title || '',
          description: form.kakaoShareBtn.description || '',
          imageUrl: form.kakaoShareBtn.imageUrl || '',
          buttonText: form.kakaoShareBtn.buttonText || '',
          btnLink: form.kakaoShareBtn.btnLink || ''
        };
      }

      // 쿠폰함 리소스 설정 추가 (URL이 있는 리소스만 포함)
      if (form.couponBoxResources && Array.isArray(form.couponBoxResources)) {
        const validResources = form.couponBoxResources.filter(resource =>
          resource.url && resource.url.trim() !== ''
        );
        if (validResources.length > 0) {
          apiData.couponBoxResources = validResources;
        }
      }

      // 이탈방지 팝업 설정 추가
      if (form.exitPreventionPopup) {
        // 모든 설정 정보를 항상 전송 (enabled 상태와 관계없이)
        apiData.exitPreventionPopup = {
          enabled: form.exitPreventionPopup.enabled || false,
          imageUrl: form.exitPreventionPopup.imageUrl || '',
          buttonImageUrl: form.exitPreventionPopup.buttonImageUrl || '',
          clickAction: form.exitPreventionPopup.clickAction || {
            actionType: 'none',
            url: '',
            target: '_blank'
          },
          buttonClickAction: form.exitPreventionPopup.buttonClickAction || {
            actionType: 'none',
            url: '',
            target: '_blank'
          }
        };
      }

      // eventPages 추가 (계층 구조 데이터가 있는 경우)
      if (form.eventPages && form.eventPages.length > 0) {
        // DTO에 정의되지 않은 필드 제거 및 데이터 정리
        apiData.eventPages = form.eventPages.map(page => {
          // EventPageDto: pageId, pageType, pageName, order, steps, metadata, isActive
          const cleanPage = {
            pageId: page.pageId,
            pageType: page.pageType,
            pageName: page.pageName,
            order: page.order,  // order 필드 추가 (API에 추가됨)
            isActive: page.isActive !== false
          };

          // metadata가 있으면 추가
          if (page.metadata && Object.keys(page.metadata).length > 0) {
            cleanPage.metadata = page.metadata;
          }

          // steps 처리
          if (page.steps && page.steps.length > 0) {
            cleanPage.steps = page.steps.map(step => {
              // PageStepDto: stepId, stepType, stepName, sections, conditions, order, isActive
              const cleanStep = {
                stepId: step.stepId,
                stepType: step.stepType,
                stepName: step.stepName,
                order: step.order,
                isActive: step.isActive !== false
              };

              // conditions가 있으면 추가
              if (step.conditions && Object.keys(step.conditions).length > 0) {
                cleanStep.conditions = step.conditions;
              }

              // sections 처리
              if (step.sections && step.sections.length > 0) {
                cleanStep.sections = step.sections.map(section => {
                  // PageSectionDto: sectionId, sectionType, sectionName, components, metadata, order
                  const cleanSection = {
                    sectionId: section.sectionId,
                    sectionType: section.sectionType,
                    sectionName: section.sectionName,  // sectionName 필드 추가 (API에 추가됨)
                    order: section.order
                  };

                  // metadata가 있으면 추가
                  if (section.metadata && Object.keys(section.metadata).length > 0) {
                    cleanSection.metadata = section.metadata;
                  }

                  // components 처리
                  if (section.components && section.components.length > 0) {
                    cleanSection.components = section.components
                      // 모든 컴포넌트 유지 (빈 content도 포함)
                      .map(comp => ({
                        // PageComponentDto: id, type, content, order, isActive
                        id: comp.id,
                        type: comp.type,
                        content: comp.content,
                        order: comp.order,
                        isActive: comp.isActive !== false
                      }));
                  } else {
                    cleanSection.components = [];
                  }

                  return cleanSection;
                }); // 모든 섹션 유지 (컴포넌트가 없어도 포함)
              } else {
                cleanStep.sections = [];
              }

              return cleanStep;
            }); // 모든 스텝 유지 (섹션이 없어도 포함)
          } else {
            cleanPage.steps = [];
          }

          return cleanPage;
        }); // 모든 페이지 유지 (스텝이 없어도 포함)
      }


      // 보상 임계치 설정 추가 (배열이 있는 경우에만)
      if (form.rewardThresholds && form.rewardThresholds.length > 0) {
        apiData.rewardThresholds = form.rewardThresholds.map(threshold => {
          const thresholdData = {
            conditionType: threshold.conditionType,
            targetAudience: threshold.targetAudience || 'all',  // 기본값: 전체 적용
            thresholdValue: threshold.thresholdValue,
            rewardName: threshold.rewardName,
            rewardDescription: threshold.rewardDescription || undefined,
            maxRewardCount: threshold.maxRewardCount === '' ? null : threshold.maxRewardCount,
            isActive: threshold.isActive !== false, // 기본값 true
            alimTalkTemplateCode: threshold.alimTalkTemplateCode || undefined,
            customConditionDescription: threshold.conditionType === 'custom' ? threshold.customConditionDescription : undefined
          };

          // couponInfo가 있으면 추가 (쿠폰 상품 선택 정보)
          if (threshold.couponInfo && threshold.couponInfo.goodsId) {
            thresholdData.couponInfo = {
              provider: threshold.couponInfo.provider || '',
              contractId: threshold.couponInfo.contractId || threshold.couponInfo.contractCode || '',
              goodsId: threshold.couponInfo.goodsId,
              goodsName: threshold.couponInfo.goodsName || '',
              brand: threshold.couponInfo.brand || '',
              brandCategory: threshold.couponInfo.brandCategory || '',
              category1: threshold.couponInfo.category1 || '',
              category2: threshold.couponInfo.category2 || '',
              goodsImg: threshold.couponInfo.goodsImg || '',
              orgPrice: threshold.couponInfo.orgPrice || 0,
              salePrice: threshold.couponInfo.salePrice || 0,
              expiry: threshold.couponInfo.expiry || 0
            };
          }

          return thresholdData;
        });
      }

      // bulkAttendeeCreation 설정 (생성 모드에서만 enabled가 true일 때 포함)
      // 수정 모드에서는 bulkAttendeeCreation 파라미터를 전송하지 않음
      if (!isEdit.value && form.bulkAttendeeCreation?.enabled) {
        apiData.bulkAttendeeCreation = {
          enabled: true,
          targetType: form.bulkAttendeeCreation.targetType,
          filters: form.bulkAttendeeCreation.filters,
          options: form.bulkAttendeeCreation.options
        };
      }

      // AARRR 메트릭 설정 추가 - 모든 단계를 항상 포함
      if (form.aarrrMetrics) {
        const cleanMetrics = {
          acquisition: [],
          activation: [],
          retention: [],
          revenue: [],
          referral: []
        };

        ['acquisition', 'activation', 'retention', 'revenue', 'referral'].forEach(stage => {
          if (form.aarrrMetrics[stage] && form.aarrrMetrics[stage].length > 0) {
            cleanMetrics[stage] = form.aarrrMetrics[stage].map(metric => {
              const cleanMetric = {
                activityType: metric.activityType // 필수 필드
              };

              // 선택 필드들 추가 (값이 있을 때만)
              if (metric.displayName) cleanMetric.displayName = metric.displayName;
              if (metric.isConversion) cleanMetric.isConversion = true;
              if (metric.weight && metric.weight !== 1) cleanMetric.weight = metric.weight;
              if (metric.conversionValue) cleanMetric.conversionValue = metric.conversionValue;
              if (metric.target) cleanMetric.target = metric.target;
              if (metric.funnelStep) cleanMetric.funnelStep = metric.funnelStep;
              if (metric.conditions && Object.keys(metric.conditions).length > 0) {
                cleanMetric.conditions = metric.conditions;
              }
              if (metric.isActive === false) cleanMetric.isActive = false;

              return cleanMetric;
            });
          }
          // else 절 제거 - 빈 배열이라도 유지
        });

        // 항상 aarrrMetrics를 포함 (빈 단계가 있더라도)
        apiData.aarrrMetrics = cleanMetrics;
      }

      // 예산 관리 설정 추가
      if (form.budgetRounds && form.budgetRounds.length > 0) {
        // 누적 총액 계산
        let cumulativeTotal = 0;
        apiData.budgetRounds = form.budgetRounds.map(round => {
          cumulativeTotal += round.amount || 0;
          return {
            roundNumber: round.roundNumber,
            amount: round.amount,
            cumulativeTotal: cumulativeTotal,
            description: round.description || '',
            startDate: round.startDate ? dayjs(round.startDate).toISOString() : undefined,
            endDate: round.endDate ? dayjs(round.endDate).toISOString() : undefined,
            status: round.status || 'planned',
            createdAt: round.createdAt || new Date().toISOString()
          };
        });
        apiData.totalBudget = cumulativeTotal;
      } else {
        apiData.budgetRounds = [];
        apiData.totalBudget = 0;
      }
      if (form.budgetNote) {
        apiData.budgetNote = form.budgetNote;
      }

      // 지출 내역 추가
      if (form.expenses && form.expenses.length > 0) {
        apiData.expenses = form.expenses.map(expense => ({
          category: expense.category,
          customCategoryName: expense.customCategoryName || undefined,
          amount: expense.amount,
          description: expense.description || '',
          expenseDate: expense.expenseDate ? dayjs(expense.expenseDate).toISOString() : new Date().toISOString(),
          createdAt: expense.createdAt || new Date().toISOString()
        }));
      } else {
        apiData.expenses = [];
      }

      // 날짜 객체를 문자열로 변환
      if (apiData.startDate) {
        apiData.startDate = dayjs(apiData.startDate).toISOString();
      }
      if (apiData.endDate) {
        apiData.endDate = dayjs(apiData.endDate).toISOString();
      }
      
      // ID가 있는 경우에만 포함
      if (isEdit.value && form.id) {
        // apiData.id = form.id; // 아이디는 폼데이터로 넘기는게 아니라 보통 엔드포인트에 넘기는 파라미터로 넘기는 것이라서 여기서는 넘기지 않음
      }
      
      console.log('서버로 전송될 데이터:', apiData);
      console.log('eventPages 데이터:', apiData.eventPages);
      
      // 주입된 API 인스턴스 사용
      if (isEdit.value) {
        api.updateEvent(
          form.id,
          apiData,
          (response) => {
            app.$message.success('이벤트가 성공적으로 수정되었습니다.');
            // 수정 완료 후 현재 페이지에 유지
            submitting.value = false;
            isDirty.value = false; // 저장 성공 후 dirty state 초기화

            // 수정된 데이터로 폼 업데이트 (필요시)
            if (response.data) {
              // 필요한 경우 응답 데이터로 폼을 업데이트할 수 있음
              console.log('이벤트 수정 완료:', response.data);
            }
          },
          (error) => {
            console.error('이벤트 수정 중 오류 발생:', error);
            app.$message.error('이벤트 수정 중 오류가 발생했습니다.');
            submitting.value = false;
          }
        );
      } else {
        api.createEvent(
          apiData, 
          (response) => {
            app.$message.success('이벤트가 성공적으로 등록되었습니다.');
            router.push('/events/management');
          }, 
          (error) => {
            console.error('이벤트 등록 중 오류 발생:', error.response?.data || error);
            app.$message.error(error.response?.data?.message || '이벤트 등록 중 오류가 발생했습니다.');
            submitting.value = false;
          }
        );
      }
    } catch (error) {
      // API 호출 또는 기타 오류 처리
      console.error('이벤트 등록/수정 중 오류 발생:', error);
      app.$message.error('이벤트 등록/수정 중 오류가 발생했습니다.');
      submitting.value = false;
    }
  };
  
  // 취소 핸들러
  const handleCancel = async () => {
    // 이동할 URL 결정 (returnUrl이 있으면 해당 URL, 없으면 기본 목록)
    const getReturnPath = () => {
      if (route.query.returnUrl) {
        // Vue Router가 자동으로 디코딩하므로 추가 디코딩 불필요
        return route.query.returnUrl;
      } else if (route.query.from === 'participation') {
        // from이 participation인 경우 참여관리 페이지로 이동
        const eventId = route.params.id;
        return `/events/participation?eventId=${eventId}`;
      }
      return '/events/management';
    };

    // 변경 사항이 없으면 바로 이동
    if (!isDirty.value) {
      router.push(getReturnPath());
      return;
    }

    // 변경 사항이 있으면 확인 대화상자 표시
    const isConfirmed = await twoBtnDialog(
      '작업 취소',
      '작성 중인 내용이 있습니다. 취소하시겠습니까?',
      '확인'
    );

    if (isConfirmed) {
      router.push(getReturnPath());
    }
  };

  // dirty state 초기화 함수 (외부에서 호출 가능)
  const resetDirtyState = () => {
    isDirty.value = false;
  };
  
  // 이미지 업로드 전 검사
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      app.$message.error('이미지 파일만 업로드할 수 있습니다!');
      return false;
    }
    
    const extension = file.name.split('.').pop().toLowerCase();
    const isValidExtension = validImageExtensions.includes(extension);
    if (!isValidExtension) {
      app.$message.error(`유효한 이미지 파일 형식이 아닙니다. (${validImageExtensions.join(', ')}) 형식만 지원합니다.`);
      return false;
    }
    
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      app.$message.error('이미지 크기는 2MB 이하여야 합니다!');
      return false;
    }
    
    return false; // 수동 업로드를 위해 항상 false 반환
  };
  
  // 선택된 거래처 처리
  const handleSelectClient = (clients) => {
    console.log('useEventForm - 선택된 거래처:', clients);
    
    if (Array.isArray(clients) && clients.length > 0) {
      // 이미 백엔드 스키마 형식인지 확인
      if (clients[0].organizerName) {
        // 이미 변환된 형식이면 그대로 사용
        form.organizers = [...clients];
      } else {
        // 이전 형식이면 변환
        form.organizers = clients.map(client => ({
          organizerId: String(client.id),
          organizerName: client.name,
          representative: client.representative || '',
          organizerCno: client.businessNumber || '',
          directInput: false
        }));
      }
    } else if (clients && !Array.isArray(clients)) {
      // 단일 객체로 받은 경우 (이전 버전 호환)
      if (clients.organizerName) {
        // 이미 변환된 형식이면 그대로 사용
        form.organizers = [clients];
      } else {
        form.organizers = [{
          organizerId: String(clients.id),
          organizerName: clients.name, 
          representative: clients.representative || '',
          organizerCno: clients.businessNumber || '',
          directInput: false
        }];
      }
    }
    
    console.log('useEventForm - 업데이트된 form.organizers:', form.organizers);
  };
  
  // 특정 기능이 활성화되어 있는지 확인 (기본값 false 반환)
  const isFeatureEnabled = (feature) => {
    return false; // 모든 기능 비활성화
  };
  
  // 수정 모드일 경우 데이터 로드
  const loadData = async () => {
    if (isEdit.value) {
      try {
        // API 호출로 이벤트 데이터 로드
        if (route.params.id) {
          api.getEventById(
            route.params.id, 
            (response) => {
              const eventData = response.data || response;
              
              // 로드된 데이터로 폼 업데이트
              Object.assign(form, eventData);

              // MongoDB는 _id를 반환하므로 form.id를 명시적으로 업데이트
              // (이벤트 변경 시 copyable ID 텍스트가 즉시 반영되도록)
              form.id = route.params.id;
          
              // eventType을 scenarioType으로 매핑 (하위 호환성)
              if (eventData.eventType) {
                // custom 타입 처리
                if (!['review', 'share'].includes(eventData.eventType)) {
                  form.scenarioType = 'custom';
                  form.customScenarioType = eventData.eventType;
                } else {
                  form.scenarioType = eventData.eventType;
                  form.customScenarioType = '';
                }
              }
              
              // 서버에서 scenarioType으로 오는 경우도 처리
              if (eventData.scenarioType) {
                form.scenarioType = eventData.scenarioType;
                if (eventData.customScenarioType) {
                  form.customScenarioType = eventData.customScenarioType;
                }
              }
          
              // 날짜 형식 변환 (문자열 -> dayjs 객체)
              if (eventData.startDate) {
                form.startDate = dayjs(eventData.startDate);
              }
              if (eventData.endDate) {
                form.endDate = dayjs(eventData.endDate);
              }

              // 환경변수 설정 키 로드 (기본값 빈 문자열)
              form.envConfigKey = eventData.envConfigKey || '';

              // 알림톡 템플릿 코드 로드 (기본값 빈 문자열)
              form.alimtalkTemplateCode = eventData.alimtalkTemplateCode || '';

              // 이벤트 적용 범위 로드 (기본값 ALL)
              form.targetScope = eventData.targetScope || 'ALL';

              // 과거 이벤트 참여자 차단 설정 로드 (객체 구조)
              // 기본 메시지 값
              const defaultMessages = {
                checkAllMessage: '감사합니다! 이미 저희 이벤트에 참여해 주셨네요. 한 분당 한 번만 참여 가능한 이벤트입니다. 다음 이벤트에서 또 만나요! 🎉',
                checkSameOrganizerMessage: '감사합니다! 이미 저희 이벤트에 참여해 주셨네요. 동일 주최자 이벤트는 한 번만 참여 가능합니다. 다음 기회에 만나요! 🎉',
                checkSpecificOrganizersMessage: '감사합니다! 이미 관련 이벤트에 참여해 주셨네요. 연계 이벤트는 한 번만 참여 가능합니다. 다음에 또 만나요! 🎉',
                checkSpecificEventsMessage: '감사합니다! 이미 관련 이벤트에 참여해 주셨네요. 연계 이벤트는 한 번만 참여 가능합니다. 다음에 또 만나요! 🎉',
              };

              if (eventData.blockPreviousParticipants && typeof eventData.blockPreviousParticipants === 'object') {
                form.blockPreviousParticipants = {
                  enabled: eventData.blockPreviousParticipants.enabled || false,
                  checkAll: eventData.blockPreviousParticipants.checkAll || false,
                  checkSameOrganizer: eventData.blockPreviousParticipants.checkSameOrganizer || false,
                  checkSpecificOrganizers: eventData.blockPreviousParticipants.checkSpecificOrganizers || false,
                  checkSpecificEvents: eventData.blockPreviousParticipants.checkSpecificEvents || false,
                  targetOrganizerIds: eventData.blockPreviousParticipants.targetOrganizerIds || [],
                  targetEventIds: eventData.blockPreviousParticipants.targetEventIds || [],
                  // 메시지 로드 (DB에 저장된 값이 없으면 기본값 사용)
                  checkAllMessage: eventData.blockPreviousParticipants.checkAllMessage || defaultMessages.checkAllMessage,
                  checkSameOrganizerMessage: eventData.blockPreviousParticipants.checkSameOrganizerMessage || defaultMessages.checkSameOrganizerMessage,
                  checkSpecificOrganizersMessage: eventData.blockPreviousParticipants.checkSpecificOrganizersMessage || defaultMessages.checkSpecificOrganizersMessage,
                  checkSpecificEventsMessage: eventData.blockPreviousParticipants.checkSpecificEventsMessage || defaultMessages.checkSpecificEventsMessage,
                  segmentFilter: eventData.blockPreviousParticipants.segmentFilter || 'ALL',
                };
              } else {
                // 기본값
                form.blockPreviousParticipants = {
                  enabled: false,
                  checkAll: false,
                  checkSameOrganizer: false,
                  checkSpecificOrganizers: false,
                  checkSpecificEvents: false,
                  targetOrganizerIds: [],
                  targetEventIds: [],
                  segmentFilter: 'ALL',
                  ...defaultMessages,
                };
              }

              // 쿠폰 재고 소진 여부 로드 (기본값 false)
              form.isCouponDepleted = eventData.isCouponDepleted || false;
              // 쿠폰 소진 메시지 로드 (기본값 설정)
              form.couponDepletedMessage = eventData.couponDepletedMessage || '쿠폰이 모두 소진되었습니다. 조금만 기다려 주세요.';
              // 고객사 레포트 노출 여부 로드 (기본값 false)
              form.isReportVisible = eventData.isReportVisible || false;

              // 환자/친구별 최대 쿠폰 수량 설정 로드 (기본값: 각 2장)
              if (eventData.couponLimits) {
                form.couponLimits = {
                  patient: {
                    maxCoupons: eventData.couponLimits.patient?.maxCoupons ?? 2,
                    description: eventData.couponLimits.patient?.description || ''
                  },
                  friend: {
                    maxCoupons: eventData.couponLimits.friend?.maxCoupons ?? 2,
                    description: eventData.couponLimits.friend?.description || ''
                  }
                };
              } else {
                // 기본값 설정
                form.couponLimits = {
                  patient: { maxCoupons: 2, description: '' },
                  friend: { maxCoupons: 2, description: '' }
                };
              }

              // 적용 대상 병원 목록 로드 (객체 배열 형태)
              if (eventData.targetHospitals && Array.isArray(eventData.targetHospitals)) {
                // 객체 배열인 경우 그대로 사용
                if (eventData.targetHospitals.length > 0 && typeof eventData.targetHospitals[0] === 'object') {
                  form.targetHospitals = eventData.targetHospitals;
                } else {
                  // 레거시 string 배열인 경우 변환 (하위 호환성)
                  form.targetHospitals = eventData.targetHospitals.map(id => ({
                    hospitalId: id,
                    hospitalName: id // 레거시 데이터는 ID만 표시
                  }));
                }
              } else {
                form.targetHospitals = [];
              }

              // 카카오 공유하기 버튼 설정 로드
              if (eventData.kakaoShareBtn) {
                form.kakaoShareBtn = {
                  title: eventData.kakaoShareBtn.title || '',
                  description: eventData.kakaoShareBtn.description || '',
                  imageUrl: eventData.kakaoShareBtn.imageUrl || '',
                  buttonText: eventData.kakaoShareBtn.buttonText || '',
                  btnLink: eventData.kakaoShareBtn.btnLink || ''
                };
              } else {
                // 기본값 설정
                form.kakaoShareBtn = {
                  title: '',
                  description: '',
                  imageUrl: '',
                  buttonText: '',
                  btnLink: ''
                };
              }

              // 쿠폰함 리소스 설정 로드 (배열 형식)
              if (eventData.couponBoxResources && Array.isArray(eventData.couponBoxResources)) {
                // 서버에서 받은 배열 데이터로 업데이트
                form.couponBoxResources = eventData.couponBoxResources;
              } else if (eventData.couponBoxResources && typeof eventData.couponBoxResources === 'object') {
                // 레거시 형식 데이터 변환 (객체 -> 배열)
                const legacyData = eventData.couponBoxResources;
                form.couponBoxResources = [
                  {
                    resourceId: 'patient-banner',
                    resourceType: 'PATIENT_BANNER',
                    name: '환자용 배너',
                    url: legacyData.bannerImage || '',
                    isActive: true
                  },
                  {
                    resourceId: 'patient-popup',
                    resourceType: 'PATIENT_POPUP',
                    name: '환자용 팝업',
                    url: legacyData.popupImage || '',
                    isActive: true
                  },
                  {
                    resourceId: 'friend-banner',
                    resourceType: 'FRIEND_BANNER',
                    name: '친구용 배너',
                    url: '',
                    isActive: true
                  },
                  {
                    resourceId: 'friend-popup',
                    resourceType: 'FRIEND_POPUP',
                    name: '친구용 팝업',
                    url: '',
                    isActive: true
                  }
                ];
              } else {
                // 기본값 설정
                form.couponBoxResources = [
                  {
                    resourceId: 'patient-banner',
                    resourceType: 'PATIENT_BANNER',
                    name: '환자용 배너',
                    url: '',
                    isActive: true
                  },
                  {
                    resourceId: 'patient-popup',
                    resourceType: 'PATIENT_POPUP',
                    name: '환자용 팝업',
                    url: '',
                    isActive: true
                  },
                  {
                    resourceId: 'friend-banner',
                    resourceType: 'FRIEND_BANNER',
                    name: '친구용 배너',
                    url: '',
                    isActive: true
                  },
                  {
                    resourceId: 'friend-popup',
                    resourceType: 'FRIEND_POPUP',
                    name: '친구용 팝업',
                    url: '',
                    isActive: true
                  }
                ];
              }


              // 이탈방지 팝업 설정 로드
              if (eventData.exitPreventionPopup) {
                form.exitPreventionPopup = {
                  enabled: eventData.exitPreventionPopup.enabled || false,
                  imageUrl: eventData.exitPreventionPopup.imageUrl || '',
                  buttonImageUrl: eventData.exitPreventionPopup.buttonImageUrl || '',
                  clickAction: eventData.exitPreventionPopup.clickAction || {
                    actionType: 'none',
                    url: '',
                    target: '_blank'
                  },
                  buttonClickAction: eventData.exitPreventionPopup.buttonClickAction || {
                    actionType: 'none',
                    url: '',
                    target: '_blank'
                  }
                };
              }

              // eventPages 로드 (계층 구조 데이터)
              if (eventData.eventPages && Array.isArray(eventData.eventPages) && eventData.eventPages.length > 0) {
                form.eventPages = eventData.eventPages;

                // 기존 이벤트에 hospitalDetailPage가 없으면 추가
                const hasHospitalDetailPage = form.eventPages.some(page => page.pageId === 'hospitalDetailPage');
                if (!hasHospitalDetailPage) {
                  form.eventPages.push({
                    pageId: 'hospitalDetailPage',
                    pageType: 'hospitalDetail',
                    pageName: '병원 상세 페이지',
                    order: 3,
                    isActive: true,
                    steps: [
                      {
                        stepId: 'mainStep',
                        stepType: 'main',
                        stepName: '메인',
                        order: 1,
                        isActive: true,
                        sections: [
                          {
                            sectionId: 'mainSection',
                            sectionName: '메인 섹션',
                            sectionType: 'content',
                            order: 1,
                            components: [],
                            metadata: {}
                          }
                        ]
                      }
                    ]
                  });
                }

                // 기존 이벤트에 reviewModal이 없으면 추가
                const hasReviewModal = form.eventPages.some(page => page.pageId === 'reviewModal');
                if (!hasReviewModal) {
                  form.eventPages.push({
                    pageId: 'reviewModal',
                    pageType: 'hospitalReviewModal',
                    pageName: '후기 모달',
                    order: 4,
                    isActive: true,
                    steps: [
                      {
                        stepId: 'mainStep',
                        stepType: 'main',
                        stepName: '메인',
                        order: 1,
                        isActive: true,
                        sections: [
                          {
                            sectionId: 'mainSection',
                            sectionName: '메인 섹션',
                            sectionType: 'content',
                            order: 1,
                            components: [],
                            metadata: {}
                          }
                        ]
                      }
                    ]
                  });
                }
              } else {
                // eventPages가 없으면 초기화
                form.eventPages = initializeEventPages();
              }

              // 보상 임계치 설정 로드 (배열)
              if (eventData.rewardThresholds && Array.isArray(eventData.rewardThresholds)) {
                form.rewardThresholds = eventData.rewardThresholds;
              } else {
                // 기본값 설정 (빈 배열)
                form.rewardThresholds = [];
              }

              // AARRR 메트릭 설정 로드
              if (eventData.aarrrMetrics) {
                form.aarrrMetrics = {
                  acquisition: eventData.aarrrMetrics.acquisition || [],
                  activation: eventData.aarrrMetrics.activation || [],
                  retention: eventData.aarrrMetrics.retention || [],
                  revenue: eventData.aarrrMetrics.revenue || [],
                  referral: eventData.aarrrMetrics.referral || []
                };
              } else {
                // 기본값 설정 (빈 배열들)
                form.aarrrMetrics = {
                  acquisition: [],
                  activation: [],
                  retention: [],
                  revenue: [],
                  referral: []
                };
              }

              // 예산 관리 설정 로드
              if (eventData.budgetRounds && Array.isArray(eventData.budgetRounds)) {
                form.budgetRounds = eventData.budgetRounds.map(round => ({
                  ...round,
                  startDate: round.startDate ? dayjs(round.startDate) : null,
                  endDate: round.endDate ? dayjs(round.endDate) : null
                }));
              } else {
                form.budgetRounds = [];
              }
              form.totalBudget = eventData.totalBudget || 0;
              form.budgetNote = eventData.budgetNote || '';

              // 지출 내역 로드
              if (eventData.expenses && Array.isArray(eventData.expenses)) {
                form.expenses = eventData.expenses.map(expense => ({
                  ...expense,
                  expenseDate: expense.expenseDate ? dayjs(expense.expenseDate) : null
                }));
              } else {
                form.expenses = [];
              }

              // 인증서 수신 페이지 모드 복원 (REQ-R3-C3, AC8-S3)
              // 구 이벤트(필드 없음)는 기본값 'event' 유지 — Object.assign이 undefined로 덮어쓸 수 있으므로 명시적 nullish 처리
              form.certificateReceivePageType = eventData.certificateReceivePageType ?? 'event';

              // 쿠폰함 팝업 표시 여부 복원 (REQ-R2-C5, AC5.2)
              // 구 이벤트(필드 없음)는 기본값 true 유지 — Object.assign이 undefined로 덮어쓸 수 있으므로 명시적 nullish 처리
              form.couponPageEventModalEnabled = eventData.couponPageEventModalEnabled ?? true;

              // 후기 화면 복원 — 구 이벤트(필드 없음)는 기본형
              form.reviewPageType = eventData.reviewPageType ?? 'default';

              // 데이터 로드 후 dirty state 초기화 (로드된 데이터는 변경 사항이 아님)
              // nextTick을 사용하여 watch가 트리거된 후에 초기화
              setTimeout(() => {
                isDirty.value = false;
              }, 0);
            },
            (error) => {
              console.error('이벤트 데이터 로드 중 오류 발생:', error);
              app.$message.error('이벤트 데이터를 불러오는 중 오류가 발생했습니다.');
            }
          );
        }
      } catch (error) {
        console.error('이벤트 데이터 로드 중 오류 발생:', error);
        app.$message.error('이벤트 데이터를 불러오는 중 오류가 발생했습니다.');
      }
    }
  };

  return {
    formRef,
    form,
    rules,
    submitting,
    isEdit,
    activeTab,
    showSearchModal,
    isDirty,

    handleSubmit,
    handleCancel,
    beforeUpload,
    handleSelectClient,
    isFeatureEnabled,
    loadData,
    resetDirtyState
  };
} 